import { isDevMode } from '@angular/core';
import type { JsonValue } from '@bufbuild/protobuf';
import { equals, fromJson, isMessage } from '@bufbuild/protobuf';
import type { GenMessage } from '@bufbuild/protobuf/codegenv2';
import type { Timestamp, TimestampJson } from '@bufbuild/protobuf/wkt';
import { CalendarOptions } from '@fullcalendar/core';
import { timestampToDate } from '@vmspdb/definitions';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export function debugResults<T>(name = 'results') {
  // const store = inject(Store);
  const stack = new Error().stack;

  return (source: Observable<T>) =>
    source.pipe(
      tap((results) => {
        const debug = true;
        if (debug) {
          console.log(
            {
              [name]: results,
            },
            stack,
          );
        }
      }),
    );
}

export function devConsoleLog(...data: any) {
  if (isDevMode()) {
    console.log(...data);
  }
}

/**
 * Formly datepicker fields bind a real `Date` object even when the rest of
 * the model is a `*Json` type (proto Timestamp fields are strings there).
 * Apply this explicitly to each field known to come from a datepicker before
 * handing the model to `fromJson`, which rejects a raw `Date`.
 */
export function dateToIsoString(
  value: Date | string | undefined,
): string | undefined {
  return value instanceof Date ? value.toISOString() : value;
}

/**
 * Converts a protobuf `Timestamp` message, its RFC3339 string (`*Json`)
 * form, or an already-`Date` value into a `Date` — the shape formly
 * datetime fields require to render/bind correctly. Nullish values pass
 * through unchanged.
 */
export function toDateValue(
  value: Date | Timestamp | TimestampJson | null | undefined,
): Date | undefined {
  return timestampToDate(value);
}

/**
 * The shape of `T` after passing `keys` through `toDateValue` — those keys
 * become `Date` (optional, since a nullish source value stays unconverted),
 * everything else is unchanged.
 */
export type WithDateFields<T, K extends keyof T> = Omit<T, K> & {
  [P in K]?: Date;
};

/** Shallow-copies `model` with each of `keys` passed through `toDateValue`. */
export function withDateFields<
  T extends Record<string, unknown>,
  K extends keyof T,
>(model: T, keys: K[]): WithDateFields<T, K> {
  const result: Record<string, unknown> = { ...model };
  for (const key of keys) {
    const converted = toDateValue(
      model[key] as Date | Timestamp | TimestampJson | null | undefined,
    );
    if (converted !== undefined) {
      result[key as string] = converted;
    }
  }
  return result as WithDateFields<T, K>;
}

/** True only for `{}`-literal objects - not Date, not a class instance. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Recursively drops nullish values from a submit payload, immediately before
 * it is handed to `fromJson`.
 *
 * - **`undefined`** (e.g. an optional datepicker field the user left empty):
 *   `fromJson` treats a present key holding `undefined` differently from an
 *   absent key and rejects it, most visibly for message-typed fields like
 *   `google.protobuf.Timestamp`.
 * - **`null`**: harmless on a scalar or message field (protobuf-es reads it as
 *   "clear this field"), but fatal inside a `map` or `repeated` - see
 *   `readListOrMapItem`'s `map value must not be null`. Reachable through e.g.
 *   `attributes` (`map<string, string>`) when its textarea is blank. Dropping
 *   it everywhere is behaviour-preserving because every caller builds a
 *   *fresh* message: clearing a field that is already unset is a no-op, so an
 *   omitted key and an explicit `null` decode identically. That holds for
 *   explicit-presence (`optional`) fields too - presence only diverges when
 *   merging into an already-populated message, which none of these paths do.
 *
 * Recurses into plain objects and arrays only. Anything else - a `Date` from a
 * Formly datepicker most of all - is passed through untouched, since recursing
 * into one would flatten it to `{}`.
 */
export function withoutUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined && item !== null)
      .map((item) => withoutUndefined(item)) as T;
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, withoutUndefined(v)]),
    ) as T;
  }
  return value;
}

/**
 * Deep-copies the plain-object/array spine of a value, so a Formly form can
 * mutate it freely.
 *
 * NgRx runs with `strictStateImmutability` (see `bootstrap.ts`), which
 * deep-freezes store state. A modal seeded with a store object via a spread
 * (`{ ...job }`) only unfreezes the *top* level: Formly then writes a nested
 * key - `model.job.permitService`, or `splice()` on `model.job.permits` when a
 * repeat row is added - straight onto a still-frozen object, which throws
 * `Cannot assign to read only property` (or, for the array, silently fails to
 * add the row in a production build).
 *
 * Unlike `structuredClone`, this cannot throw on an unclonable value: anything
 * that is not a plain object or array - a `Date` from a datepicker most of all
 * - is carried over by reference, since none of those are what Formly mutates
 * in place.
 */
export function deepClonePlain<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => deepClonePlain(item)) as T;
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, deepClonePlain(v)]),
    ) as T;
  }
  return value;
}

/**
 * Kebab-cases arbitrary text for use in a stable, static CSS class or
 * data-testid, e.g. a modelName like "Project Job" -> "project-job".
 */
export function slug(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * `compareWith` for a Formly/mat-select field bound to a protobuf message
 * type, where the selected value and the option value may arrive in
 * different shapes (a raw proto message vs. its `*Json` form) — e.g. a
 * form control seeded from a store/RPC value alongside options built with
 * `toJson`. Normalizes both sides to a message via the schema before using
 * protobuf-es's structural `equals`, rather than comparing shapes directly.
 */
export function messageCompareWith<Desc extends GenMessage<any>>(schema: Desc) {
  type T = Desc extends GenMessage<infer M> ? M : never;
  const normalize = (value: unknown): T | undefined => {
    if (value === null || value === undefined) {
      return undefined;
    }
    return isMessage(value, schema)
      ? value
      : fromJson(schema, value as JsonValue, { ignoreUnknownFields: true });
  };
  return (o1: unknown, o2: unknown): boolean => {
    const a = normalize(o1);
    const b = normalize(o2);
    if (!a || !b) {
      return a === b;
    }
    return equals(schema, a, b);
  };
}

export function getAppName() {
  return (environment.firebase.projectId || window.location.hostname).match(
    /vms/,
  )
    ? 'vms-web-app'
    : 'civil-smart';
}

// See https://github.com/ReactiveX/rxjs/issues/5569#issuecomment-653852833
// Returns an observable that completes
export const VOID = new Observable<void>((observer) => {
  observer.next();
  observer.complete();
});

export function getFormErrors(form: any) {
  if (!form.controls) {
    // Return FormControl errors or null
    return form.errors ?? null;
  }

  const groupErrors = form.errors;
  // Form group can contain errors itself, in that case add 'em
  const formErrors: any = groupErrors ? { groupErrors } : {};
  Object.keys(form.controls).forEach((key) => {
    // Recursive call of the FormGroup fields
    const error = getFormErrors(form.get(key));
    if (error !== null) {
      // Only add error if not null
      formErrors[key] = error;
    }
  });
  // Return FormGroup errors or null
  return Object.keys(formErrors).length > 0 ? formErrors : null;
}

export declare type CalendarOption<OptionName> =
  OptionName extends keyof CalendarOptions
    ? CalendarOptions[OptionName]
    : never;
